import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import CurrencyDropdown from 'src/components/CurrencyDropdown/CurrencyDropdown'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import { ExtendCurrency } from 'src/enums'
import Crncy, { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { selectNullSafeCurrencyFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface AmountInputFieldProps {
  autoComplete?: string
  baseCurrency?: ExtendCurrency
  baseValue?: number
  className?: string
  currency?: ExtendCurrency
  disabled?: boolean
  errors?: Array<React.ReactElement | string>
  fieldLabel?: React.ReactNode
  isTextarea?: boolean
  maxValue: number
  name?: string
  onChange: (value: number | undefined) => void
  showUpdates?: boolean
  type?: string
  value?: number
  onCurrencyChange?: (value: ExtendCurrency) => void
  isRequest: boolean
  unitOfLength?: string
  placeholder?: string
  optional?: boolean
  tooltip?: React.ReactElement
  static?: boolean
  testId?: string
  integerOnly?: boolean
}

const AmountInputField: React.FC<AmountInputFieldProps> = (
  props,
): React.ReactNode => {
  const translate = useTranslate()

  const defaultCurrency = useSelector(selectNullSafeCurrencyFromState)
  const currencyFormatter = useCurrencyFormatter()

  const inputValue = props.value !== undefined ? props.value : ''

  const [showErrors, setShowErrors] = useState(props.errors !== undefined)
  useEffect((): void => {
    setShowErrors(props.errors !== undefined)
  }, [props.errors])

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    setShowErrors(false)
    const initialString = props.integerOnly
      ? e.target.value.replace(/\./g, '')
      : e.target.value

    const formattedString = props.unitOfLength
      ? initialString.indexOf('.') >= 0
        ? initialString.slice(0, initialString.indexOf('.') + 2)
        : initialString
      : initialString

    const parsedValue = parseFloat(formattedString)
    const value =
      parsedValue !== undefined && !isNaN(parsedValue)
        ? Math.min(parsedValue, props.maxValue)
        : undefined

    props.onChange(value)
  }

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (props.integerOnly && e.key === '.') {
      e.preventDefault()
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (document.activeElement === inputRef.current) {
        inputRef.current?.blur()
      }
      return
    }
  }

  const [isInputFocused, setIsInputFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const onInputFocus = (): void => {
    setIsInputFocused(true)
  }
  const onInputBlur = (): void => {
    setIsInputFocused(false)
  }

  useEffect((): void => {
    if (isInputFocused) {
      inputRef.current?.select()
    }
  }, [isInputFocused])

  const formattedInput = (val: number | undefined): string => {
    return val !== undefined && props.currency !== undefined
      ? currencyFormatter({
          amount: val,
          currency: props.currency,
          forceHideCurrency: true,
          maximumFractionDigits: props.integerOnly ? 0 : 2,
        })
      : ''
  }

  const lengthFormattedInput = (val: number | undefined): string => {
    return val !== undefined && props.unitOfLength
      ? val === Math.round(val)
        ? val.toString()
        : val.toFixed(1)
      : ''
  }

  const isUpdated = !!props.showUpdates && props.baseValue !== props.value

  return (
    <div
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;

        input::-webkit-outer-spin-button {
          -webkit-appearance: none;
          appearance: none;
          margin: 0;
        }

        input[type='number'] {
          -moz-appearance: textfield;
        }
      `}
    >
      {props.fieldLabel !== undefined && (
        <FieldLabel
          css={css`
            z-index: 1;
            display: flex;
            align-items: center;
          `}
          isUpdated={isUpdated}
          dontHideOverflow
        >
          {isUpdated ? (
            props.baseValue !== undefined ? (
              <>
                {props.unitOfLength !== undefined &&
                  translate('${fieldLabel} (was "${baseValue}")', {
                    fieldLabel: props.fieldLabel,
                    baseValue: props.baseValue.toString(),
                  })}
                {props.baseCurrency !== undefined &&
                  translate('${fieldLabel} (was ${baseValue})', {
                    fieldLabel: props.fieldLabel,
                    baseValue: (
                      <Crncy
                        currency={props.baseCurrency}
                        amount={props.baseValue}
                      />
                    ),
                  })}
              </>
            ) : (
              <>{props.fieldLabel}</>
            )
          ) : (
            props.fieldLabel
          )}
          {props.tooltip !== undefined && !props.static && props.tooltip}
        </FieldLabel>
      )}
      {props.static && (
        <div>
          {props.currency && props.value ? (
            <Crncy currency={props.currency} amount={props.value} />
          ) : (
            '--'
          )}
        </div>
      )}
      {!props.static && (
        <>
          <span
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              caret-color: ${themeConstants.colors.cta};
              flex-grow: 2;
              width: 100%;
              border-radius: 6px;
              height: 50px;
              border: 1px solid ${styleConstants.colors.gray_medium};
              background-color: ${styleConstants.colors.gray_lightest};

              & > * {
                background-color: inherit;
                border-radius: inherit;
                border: 0px;
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};

                &::placeholder {
                  ${getFontCss(
                    14,
                    styleConstants.colors.gray_dark,
                    styleConstants.fontFamilies.regular,
                  )};
                }
              }

              & > :first-child {
                border-right: inherit;
                border-top-right-radius: 0px;
                border-bottom-right-radius: 0px;
              }

              &:focus,
              &:hover {
                background-color: ${styleConstants.colors.white};
                border: 1px solid ${themeConstants.colors.cta};
              }

              &::placeholder {
                opacity: 0.5;
              }

              ${isUpdated &&
              css`
                border: 1px solid ${themeConstants.colors.highlight};

                &:focus-within,
                &:hover {
                  border: 1px solid ${themeConstants.colors.highlight};
                }
              `}

              ${showErrors &&
              css`
                border: 1px solid ${styleConstants.colors.red};
              `}
            `}
          >
            {props.isRequest && props.onCurrencyChange ? (
              <CurrencyDropdown
                className={'currency-input'}
                css={css`
                  min-width: 83px;
                  padding: 0;
                  border: 0;
                  border-right: inherit;
                  border-radius: inherit;
                `}
                selectedValue={
                  props.currency !== undefined
                    ? props.currency
                    : defaultCurrency
                }
                chooseOption={props.onCurrencyChange}
                smallDisplay={true}
                rootContainerCss={css`
                  border: 0;
                  background-color: inherit;
                  height: 100%;
                  min-height: 48px;

                  &:focus-within,
                  &:hover {
                    border: 0;
                  }
                `}
              />
            ) : (
              <div
                css={css`
                  width: 68px;
                  height: 100%;
                  display: flex;
                  align-items: center;
                  padding: 0px 20px;
                  color: ${isUpdated && themeConstants.colors.highlight};
                `}
              >
                {props.unitOfLength
                  ? props.unitOfLength
                  : props.currency
                    ? props.currency
                    : defaultCurrency}
              </div>
            )}
            <input
              css={css`
                height: 100%;
                width: 100%;
                display: flex;
                align-items: center;
                padding: 0px 20px;
                color: ${isUpdated && themeConstants.colors.highlight};
              `}
              ref={inputRef}
              className={'input'}
              placeholder={
                props.placeholder !== undefined ? props.placeholder : '0'
              }
              value={
                isInputFocused
                  ? inputValue
                  : props.unitOfLength
                    ? lengthFormattedInput(props.value)
                    : formattedInput(props.value)
              }
              type={isInputFocused ? 'number' : 'text'}
              step={props.integerOnly ? 1 : 0.01}
              onBlur={onInputBlur}
              onChange={onChange}
              disabled={props.disabled}
              onFocus={onInputFocus}
              autoComplete={props.autoComplete || 'off'}
              name={props.name}
              inputMode={props.integerOnly ? 'numeric' : 'decimal'}
              onKeyDown={handleKeyPress}
              onWheel={(e): void => e.currentTarget.blur()}
              id={'amountInput'}
              data-testid={
                props.testId
                  ? props.testId
                  : props.name
                    ? `${props.name}-input`
                    : 'amount-input'
              }
            />
          </span>
          {showErrors && props.errors !== undefined && (
            <ErrorList errors={props.errors} />
          )}
        </>
      )}
    </div>
  )
}

export default React.memo(AmountInputField)
